/**
 * Step 5: Store Logo Upload
 */

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, ChevronLeft, Upload, Image, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StoreLogoStep({ data, onComplete, onBack }) {
  const [logo, setLogo] = useState(data?.logo_storage_path || null);
  const [previewUrl, setPreviewUrl] = useState(data?.logo_url || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/retailer/onboarding/logo/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setLogo(result.storage_path || true);
        toast.success('Logo uploaded!');
      } else {
        toast.error(result.detail || 'Failed to upload logo');
        setPreviewUrl(null);
      }
    } catch (error) {
      toast.error('Failed to upload logo');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    setLogo(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/logo/skip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        onComplete(result);
      } else {
        toast.error(result.detail || 'Failed to continue');
      }
    } catch (error) {
      toast.error('Failed to continue');
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = () => {
    onComplete({ logo_uploaded: true });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-gray-600">
          Upload your store logo. This will be displayed to customers when they browse your store.
        </p>
      </div>

      {/* Logo upload area */}
      <div className="flex flex-col items-center">
        {previewUrl ? (
          <div className="relative">
            <div className="w-48 h-48 rounded-xl overflow-hidden border-2 border-emerald-200 shadow-lg">
              <img
                src={previewUrl}
                alt="Store logo preview"
                className="w-full h-full object-cover"
              />
            </div>
            <button
              type="button"
              onClick={removeLogo}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow"
            >
              <X className="w-4 h-4" />
            </button>
            {logo && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Uploaded
              </div>
            )}
          </div>
        ) : (
          <label className="cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".jpg,.jpeg,.png,.webp,.gif"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              disabled={uploading}
            />
            <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
              {uploading ? (
                <>
                  <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                  <span className="text-sm text-gray-500">Uploading...</span>
                </>
              ) : (
                <>
                  <Image className="w-10 h-10 text-gray-400" />
                  <div className="text-center">
                    <span className="text-sm font-medium text-emerald-600">Click to upload</span>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP up to 5MB</p>
                  </div>
                </>
              )}
            </div>
          </label>
        )}
      </div>

      {/* Guidelines */}
      <div className="bg-gray-50 rounded-lg p-4">
        <Label className="text-sm font-medium mb-2 block">Logo Guidelines</Label>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Square images work best (e.g., 512x512 pixels)</li>
          <li>• Use a transparent background if possible</li>
          <li>• Keep text minimal for better visibility at small sizes</li>
          <li>• High contrast colors improve recognition</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4">
        {onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          {!logo ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleSkip}
              disabled={saving || uploading}
              data-testid="skip-logo-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Skip for Now
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleContinue}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-logo-btn"
            >
              Continue
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
